import { Injectable } from "@angular/core";
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from "@angular/common/http";
import { concatMap, from, mergeMap, Observable, of } from "rxjs";
import { SharedService } from "../services/shared.service";
import { environment } from "../../environments/environment";
import { OfficeLoginService } from "../services/officeLogin.service";
import { EtagEntitiesEnum } from "../models/shared.model";

@Injectable()
export class OfficeOutlookApiInterceptor implements HttpInterceptor {
  constructor(
    private sharedService: SharedService,
    private officeLoginService: OfficeLoginService
  ) {
  }

  private getToken(): Promise<string> {
    return new Promise((resolve, reject) => {
      this.sharedService.getToken().then(value => resolve(value));
    });
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return from(this.getToken()).pipe(
      concatMap((token: string) => {

        if(token == null) {
          return of(
            new HttpResponse({
              status: 0,
              body: { message: 'CANCELED' }
            })
          );
        }

        request = this.setToken(request, token);

        return from(this.updateUrlIfSharedFolder(request)).pipe(
          mergeMap((finalReq) => next.handle(finalReq))
        );
      })
    );
  }

  private async updateUrlIfSharedFolder(request: HttpRequest<any>) {
    const owner = await this.sharedService.getOwnerOfSharedFolder();

    //This means it's not a shared folder and is a personal folder
    if(owner == null)
      return request;

    //In case of getting exchange history, we check the user's exchange with the sender, not the shared folder email with the sender
    if(decodeURIComponent(request.url).includes(`$search="from`) || decodeURIComponent(request.url).includes(`$search="to`))
      return request;

    const sharedUserAPI = environment.apiConfig.microsoft_graph_url + `/users/${owner}`

    request = request.clone({
      url: request.url.replace(environment.apiConfig.microsoft_graph_url_personal, sharedUserAPI)
    });

    return request;
  }

  private setToken(request: HttpRequest<any>, token: string) {
    if (request.url.startsWith(environment.backendUrl)) {
      request = this.setEtagHeader(request);

      request = request.clone({
        setHeaders: {
          "X-Mailguard-Token": this.officeLoginService.idToken
        }
      });

      return request;
    }

    request = request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`
      }
    });

    return request;
  }

  private setEtagHeader(request: HttpRequest<any>){
    let etag = null;

    if (request.url.includes("settings"))
      etag = this.sharedService.getEtagValue(EtagEntitiesEnum.SETTINGS);

    if(request.url.includes("batch/all"))
      etag = this.sharedService.getEtagValue(EtagEntitiesEnum.BATCH_ALL);

    if(request.url.includes("user/me"))
      etag = this.sharedService.getEtagValue(EtagEntitiesEnum.PROFILE);

    if(request.url.includes("terms-and-conditions"))
      etag = this.sharedService.getEtagValue(EtagEntitiesEnum.TERMS_AND_CONDITION);

    if (!etag)
      return request;

    request = request.clone({
      setHeaders: {
        "If-Non-Match": etag
      }
    });

    return request;
  }
}
